import { create } from 'zustand';
import { obter, requisitar } from '../lib/api.js';
// `dicaCaixaFechada` (abaixo) usa `quantidadeBr` e `moedaBr`, e a reexportação
// lá do fim não traz os nomes para o escopo deste módulo — só os repassa.
import { moedaBr, quantidadeBr } from '../lib/formato.js';

/**
 * Estado do gerador de orçamento — Story 8.3.
 *
 * ## Por que Zustand aqui (e Context no auth)
 *
 * O cabeçalho, a lista de itens e a barra de totais são componentes distintos
 * que leem e escrevem o **mesmo** orçamento. Passar isso por props criaria uma
 * cadeia de repasse a cada nível; um Context mudaria tudo que consome a cada
 * tecla digitada num campo de quantidade. A sessão não tem esse problema — daí
 * a divisão registrada no `CLAUDE.md`.
 *
 * ## O front NÃO calcula
 *
 * Nenhum valor desta store é derivado no cliente. Toda mutação (adicionar,
 * editar, remover) recarrega o orçamento do servidor e substitui itens e
 * totais pelo que ele respondeu. A ordem canônica do RF-19 tem arredondamento
 * por caixa fechada, fator de cartão e RT por item — reimplementar isso aqui
 * seria criar uma segunda verdade que diverge na terceira casa e mente para o
 * vendedor.
 */

/** Uma opcao do seletor de coluna do del credere, pronta para exibir. */
export interface OpcaoDelCredere {
  nivel: string;
  /** `5,5% · DEL 20%` — o texto que o vendedor le. */
  rotulo: string;
  /** Preco-base a vista daquela coluna. */
  preco: string;
}

export interface ItemOrcamento {
  itemId: string;
  productId: string;
  referencia: string;
  formato: string | null;
  unidade: string;
  quantidade: string;
  desconto: string;
  /**
   * Parcela de RT do item, **efetiva**: o override manual quando existe,
   * senao o calculado pelo servidor (`com_desc × RT%`).
   */
  parcelaRt: string;
  /** Parcela de frete do item, efetiva (override ou `R$/kg × peso`). */
  parcelaFrete?: string;
  /**
   * O que o servidor TERIA calculado, ignorando os overrides.
   *
   * A tela precisa deles para dois fins: marcar de quanto foi o ajuste e
   * reconhecer quando o vendedor digita de volta o valor original — caso em
   * que o override e REMOVIDO, nao regravado com duas casas a menos.
   *
   * Opcionais porque o orcamento EMITIDO vem do snapshot, onde nao ha o que
   * recalcular — e la as celulas nem sao editaveis.
   */
  parcelaRtCalculada?: string;
  parcelaFreteCalculada?: string;
  /** `true` quando a parcela veio de override manual do item (item 03). */
  rtManual?: boolean;
  freteManual?: boolean;
  /**
   * Nivel del credere EFETIVO do item (`del_3`…`del_7`) — Epico 9.
   *
   * Nulo fora do quadrante. E o que o seletor marca como selecionado.
   */
  delNivel?: string | null;
  /**
   * As opcoes do seletor, ORDENADAS E ROTULADAS PELO SERVIDOR.
   *
   * O pareamento coluna x condicao comercial tem uma definicao so, no
   * backend. O front nao remonta rotulo nem reordena: fazer isso criaria uma
   * segunda verdade sobre qual coluna e qual, e o sintoma seria o preco certo
   * com a etiqueta errada.
   */
  precosDelCredere?: OpcaoDelCredere[];
  unit: string;
  qtdFaturada: string;
  totalItem: string;
  peso: string;
  priceSourceKey: string;
  baseAvista: string;
  /** `true` quando a base veio de override manual (R3). */
  precoManual: boolean;
  /** Justificativa já registrada do override — a tela mostra em vez de pedir de novo. */
  precoManualMotivo: string | null;
  /** Medida de uma caixa (m² ou metro linear) — insumo da dica de caixa fechada. */
  medidaCaixa: string | null;
  /**
   * Como o item é faturado — S11. `barra` (o rodapé, 2,40 m) ou `caixa`.
   *
   * No rascunho vem do catálogo; no emitido, do SNAPSHOT da emissão — é por
   * isso que a dica de um emitido antigo continua dizendo "caixa" mesmo
   * depois de o produto passar a ser vendido por barra.
   *
   * Opcional pelo deploy escalonado: web novo com API antiga não recebe o
   * campo, e ausente lê-se como `caixa`, que era o único modo.
   */
  unidadeVenda?: 'caixa' | 'barra';
  /** O tamanho da barra (`2.4000`); nulo na caixa. */
  medidaArredondamento?: string | null;
  /**
   * Cômodo da obra ("COZINHA") — S9. Nulo = não informado.
   *
   * Opcional pelo deploy escalonado: web novo com API antiga não recebe o
   * campo, e ausente lê-se como vazio.
   */
  ambiente?: string | null;
}

/**
 * Item do rascunho que o servidor **não conseguiu precificar**.
 *
 * Fica fora dos totais e impede a emissão. A tela precisa mostrá-lo com
 * destaque: é a única forma de o vendedor saber que tem de removê-lo.
 */
export interface ItemSemPreco {
  itemId: string;
  productId: string;
  referencia: string;
  quantidade: string;
  motivo: 'sem_preco' | 'sem_regra';
}

export interface TotaisOrcamento {
  medidaTotal: string;
  pesoTotal: string;
  valorTotal: string;
  rtTotal: string;
  freteTotal: string;
}

export interface Orcamento {
  id: string;
  numero: string | null;
  status: string;
  clientId: string;
  clienteNome: string;
  clienteDocumento: string | null;
  tabela: string;
  paymentType: string;
  criadoEm: string;
  emitidoEm: string | null;
  validadeAte: string | null;
  /** Vem do servidor (status === 'rascunho'); a UI reflete, não reimplementa. */
  editavel: boolean;
  /** Nivel del credere padrao do orcamento — nulo fora do quadrante. */
  delNivelPadrao?: string | null;
  /**
   * O rotulo do nivel padrao ('5% · DEL 15%') — Story do preco no seletor.
   *
   * Vem do servidor junto do codigo, e nao de uma busca separada: o cabecalho
   * mostrava `del_5` cru, e traduzir no front exigiria uma query nova numa
   * tela que usa Zustand — obrigando todo teste dela a montar um
   * QueryClientProvider por causa de um texto.
   */
  delNivelPadraoRotulo?: string | null;
  /**
   * A SEÇÃO de onde o pedido veio — S5. `null` é a seção comum.
   *
   * É o que permite ao editor voltar para a lista certa e à busca de produto
   * oferecer a classe da seção. Vem do servidor pelo mesmo motivo do `editavel`:
   * quem decide é o dado, a UI obedece.
   */
  segmento?: string | null;
  /**
   * RT do orçamento como **fração** (`0.07` = 7%). `null` = usa o do tenant.
   *
   * A tela mostra PERCENTUAL e o banco guarda FRAÇÃO — a conversão fica na
   * borda (`fracaoParaPercentual` / `percentualParaFracao`), e em lugar
   * nenhum além dela. Foi a confusão entre as duas que produziu o erro do
   * ORC-2026-0007.
   */
  rtPercentManual?: string | null;
  /** Frete do orçamento em R$/kg. `null` = usa o do tenant. */
  freteRsKgManual?: string | null;
  /**
   * O que vale quando não há override — vem do `commercial_param` do tenant.
   *
   * Opcionais de propósito: num deploy escalonado o web sobe antes da API, e um
   * campo ausente não pode apagar a tela do orçamento inteiro (a mesma razão do
   * `semPreco`).
   */
  rtPercentPadrao?: string | null;
  freteRsKgPadrao?: string | null;
  /**
   * O orçamento aplica o IPI dos produtos? — S8. `false` zera o IPI de todos
   * os itens.
   *
   * Opcional pelo mesmo motivo dos padrões acima (deploy escalonado), e
   * AUSENTE lê-se como `true`: é o comportamento de antes da S8, e uma API
   * antiga não sabe desligar IPI nenhum.
   */
  aplicarIpi?: boolean;
  itens: ItemOrcamento[];
  totais: TotaisOrcamento;
  /**
   * Itens que o servidor nao conseguiu precificar (fora dos totais).
   *
   * Opcional de proposito: num deploy escalonado o web sobe antes da API, e um
   * campo ausente nao pode apagar a tela do orcamento inteiro.
   */
  semPreco?: ItemSemPreco[];
}

export interface EdicaoItem {
  quantidade?: string;
  desconto?: string;
  precoManual?: string | null;
  precoManualMotivo?: string | null;
  /*
   * `rtManual`/`freteManual` saíram — Story C.
   *
   * O override virou do ORÇAMENTO (`definirParametros`), e desde a Story B a
   * API RECUSA os dois no PATCH de item com 400. Mantê-los aqui deixaria o
   * tipo convidando para uma chamada que só pode falhar.
   */
  /** Nivel del credere do item. `null` devolve o item ao padrao do orcamento. */
  delNivel?: string | null;
  /** Cômodo da obra — S9. `null` limpa; o servidor apara e grava vazio como nulo. */
  ambiente?: string | null;
}

/** A condição de destino escolhida em "Duplicar como…". */
export interface CondicaoDuplicacao {
  tabela?: string;
  paymentType?: string;
  /** Obrigatório quando a tabela destino é del credere. */
  delNivelPadrao?: string | null;
}

export interface ResultadoDuplicacao {
  id: string;
  /** Referências que a tabela destino não vende — vazio quando tudo coube. */
  semPreco: ItemSemPreco[];
}

interface EstadoGerador {
  orcamento: Orcamento | undefined;
  carregando: boolean;
  /** `true` durante uma mutação — trava os botões sem apagar a tela. */
  salvando: boolean;
  /**
   * `true` só durante a EMISSÃO — separado de `salvando`.
   *
   * O botão dizia "Emitindo…" sempre que `salvando` estivesse ligado, inclusive
   * quando só se salvava o frete. Anunciar uma emissão que não começou é pior
   * que não anunciar nada: a ação é irreversível, e o vendedor lê aquilo como
   * "já foi".
   */
  emitindo: boolean;
  erro: unknown;

  carregar: (id: string) => Promise<void>;
  adicionarItem: (productId: string, quantidade: string) => Promise<void>;
  /** `false` quando a API recusou — quem chamou decide o que fazer na tela. */
  editarItem: (itemId: string, edicao: EdicaoItem) => Promise<boolean>;
  removerItem: (itemId: string) => Promise<void>;
  /**
   * RT (%) e frete (R$/kg) do ORÇAMENTO — Story C.
   *
   * Recebe e envia **fração** para o RT: a conversão de percentual acontece no
   * componente, junto do campo que o usuário vê. `null` limpa o override.
   *
   * Recarrega o orçamento depois, porque mudar qualquer um dos dois muda o
   * total de TODOS os itens — é o que o `mutar` já faz para as outras edições.
   */
  definirParametros: (p: {
    rtPercentManual?: string | null;
    freteRsKgManual?: string | null;
    /** S8 — a mesma rota e o mesmo recarregamento do RT e do frete. */
    aplicarIpi?: boolean;
  }) => Promise<boolean>;
  emitir: () => Promise<{ numero: string }>;
  /**
   * Duplica o orçamento, opcionalmente em OUTRA condição.
   *
   * Sem argumento, é a duplicação de sempre — mesma tabela e pagamento. Com
   * `condicao`, o novo rascunho nasce na condição escolhida e os preços saem
   * recalculados; `semPreco` traz as referências que a tabela destino não
   * vende, para a tela avisar antes de o vendedor abrir o rascunho.
   */
  duplicar: (condicao?: CondicaoDuplicacao) => Promise<ResultadoDuplicacao>;
  limpar: () => void;
}

export const useGerador = create<EstadoGerador>((set, get) => ({
  orcamento: undefined,
  carregando: false,
  salvando: false,
  emitindo: false,
  erro: undefined,

  async carregar(id) {
    /*
     * A store é um singleton e as respostas podem chegar fora de ordem.
     *
     * `aberto` marca qual orçamento a tela está pedindo AGORA. Uma resposta
     * antiga que chega depois (usuário navegou de A para B, ou clicou em
     * Duplicar) é descartada — sem isso, o orçamento B podia ser sobrescrito
     * pelos dados do A, com a tela mostrando itens de um e cabeçalho de outro.
     */
    aberto = id;
    set({ carregando: true, erro: undefined, orcamento: undefined });
    try {
      const dados = await obter<Orcamento>(`/api/orcamentos/${id}`);
      if (aberto !== id) return;
      set({ orcamento: dados, carregando: false });
    } catch (erro) {
      if (aberto !== id) return;
      set({ erro, carregando: false });
    }
  },

  /**
   * Toda mutação segue o mesmo desenho: chama a API, **recarrega** e substitui.
   * Sem atualização otimista — o total depende de arredondamento por caixa que
   * só o servidor sabe fazer, e mostrar um número que muda sozinho meio segundo
   * depois é pior que esperar.
   */
  async adicionarItem(productId, quantidade) {
    const atual = get().orcamento;
    if (atual === undefined) return;
    await mutar(set, get, () =>
      requisitar(`/api/orcamentos/${atual.id}/itens`, {
        metodo: 'POST',
        corpo: { productId, quantidade },
      }),
    );
  },

  async editarItem(itemId, edicao) {
    const atual = get().orcamento;
    if (atual === undefined) return false;
    return mutar(set, get, () =>
      requisitar(`/api/orcamentos/${atual.id}/itens/${itemId}`, {
        metodo: 'PATCH',
        corpo: edicao,
      }),
    );
  },

  async definirParametros(parametros) {
    const atual = get().orcamento;
    if (atual === undefined) return false;
    return mutar(set, get, () =>
      requisitar(`/api/orcamentos/${atual.id}`, { metodo: 'PATCH', corpo: parametros }),
    );
  },

  async removerItem(itemId) {
    const atual = get().orcamento;
    if (atual === undefined) return;
    await mutar(set, get, () =>
      requisitar(`/api/orcamentos/${atual.id}/itens/${itemId}`, { metodo: 'DELETE' }),
    );
  },

  async emitir() {
    const atual = get().orcamento;
    if (atual === undefined) throw new Error('nenhum orcamento carregado');

    /*
     * ⚠️ ESPERA o ajuste que ainda está no ar antes de emitir.
     *
     * Emitir congela o preço no banco (D2) e o orçamento vira imutável. Se um
     * PATCH de RT/frete estiver a caminho, o servidor calcularia o snapshot com
     * o valor ANTERIOR — e o PDF sai errado, sem volta.
     *
     * Acontece pelo caminho mais banal: o `mousedown` no botão tira o foco do
     * campo, o `blur` dispara o PATCH, e o `click` chega antes da resposta.
     */
    await aguardarSalvamentos();

    set({ salvando: true, emitindo: true, erro: undefined });
    try {
      const r = await requisitar<{ numero: string }>(`/api/orcamentos/${atual.id}/emitir`, {
        metodo: 'POST',
      });
      await get().carregar(atual.id);
      return r.dados;
    } finally {
      set({ salvando: false, emitindo: false });
    }
  },

  async duplicar(condicao) {
    const atual = get().orcamento;
    if (atual === undefined) throw new Error('nenhum orcamento carregado');
    set({ salvando: true, erro: undefined });
    try {
      const r = await requisitar<ResultadoDuplicacao>(
        `/api/orcamentos/${atual.id}/duplicar`,
        // Sem condição, o corpo não vai — e a rota trata como "mesma condição",
        // que é o contrato antigo.
        condicao === undefined ? { metodo: 'POST' } : { metodo: 'POST', corpo: condicao },
      );
      // `semPreco` é opcional na leitura por causa do deploy escalonado: o web
      // pode subir antes da API que passou a devolvê-lo.
      return { id: r.dados.id, semPreco: r.dados.semPreco ?? [] };
    } finally {
      set({ salvando: false });
    }
  },

  limpar() {
    aberto = undefined;
    emVoo = 0;
    set({
      orcamento: undefined,
      carregando: false,
      salvando: false,
      emitindo: false,
      erro: undefined,
    });
  },
}));

/** Orçamento que a tela pede agora — descarta resposta de um anterior. */
let aberto: string | undefined;

/**
 * Mutações em voo.
 *
 * `salvando` era booleano: com duas mutações simultâneas (o vendedor sai de um
 * campo e clica em Remover), a **primeira** a terminar destravava a tela
 * inteira enquanto a outra ainda corria. Contador zera só quando a última
 * volta.
 */
let emVoo = 0;

/**
 * As promessas das mutações em voo — para quem precisa **esperar** por elas.
 *
 * ## A corrida que isto fecha
 *
 * RT e frete salvam no `blur`. O `mousedown` em "Emitir" tira o foco do campo,
 * então o PATCH começa; o `click` é um evento posterior e pode chegar antes da
 * resposta. E a emissão **não lê o valor da tela**: `emitirOrcamento` chama
 * `calcularOrcamento(db, …)`, que consulta o banco. Com o PATCH ainda no ar, o
 * snapshot congela o valor ANTERIOR — e o orçamento é imutável, com o PDF a
 * caminho do cliente.
 *
 * O `disabled={salvando}` do botão já fechava essa janela, mas por acaso: ele
 * está ali para evitar duplo-clique durante a emissão. Isto aqui é a trava
 * explícita, e vale para qualquer chamador — inclusive um atalho de teclado ou
 * um fluxo futuro que não passe por aquele botão.
 */
const promessasEmVoo = new Set<Promise<unknown>>();

/**
 * Espera todo salvamento pendente terminar.
 *
 * `while` e não um `await` só: uma mutação pode disparar outra enquanto se
 * espera (o recarregamento que `mutar` faz no fim, por exemplo). Sai quando o
 * conjunto esvazia de verdade.
 *
 * `allSettled` porque o que importa é **ter terminado**, não ter dado certo: um
 * PATCH que falhou não gravou nada, e a emissão vai ler do banco o que o banco
 * tem. Quem falhou já mostrou o erro na tela.
 */
export async function aguardarSalvamentos(): Promise<void> {
  while (promessasEmVoo.size > 0) {
    await Promise.allSettled([...promessasEmVoo]);
  }
}

type Set = (parcial: Partial<EstadoGerador>) => void;
type Get = () => EstadoGerador;

/**
 * Executa a mutação, recarrega do servidor e propaga o erro para a tela.
 *
 * Devolve **se deu certo**. O erro continua indo para o `erro` da store (que é
 * onde a faixa geral da tela lê), mas quem chamou também precisa saber: um
 * painel que fecha sozinho depois de uma falha mostra o valor antigo como se
 * fosse o novo — era a dívida D-02, e ela caía justo no campo que mexe em
 * dinheiro.
 */
function mutar(set: Set, get: Get, acao: () => Promise<unknown>): Promise<boolean> {
  const id = get().orcamento?.id;
  if (id === undefined) return Promise.resolve(false);

  emVoo += 1;
  set({ salvando: true, erro: undefined });

  /*
   * A promessa é registrada ANTES de qualquer `await`, e de forma síncrona.
   *
   * É o que fecha a corrida: o `blur` roda no `mousedown`, então quando o
   * `click` do "Emitir" chegar — evento posterior — a promessa já está no
   * conjunto e `aguardarSalvamentos()` a enxerga. Registrar depois de um
   * `await` deixaria exatamente a fresta que se quer fechar.
   */
  const promessa = (async (): Promise<boolean> => {
    try {
      await acao();
      const dados = await obter<Orcamento>(`/api/orcamentos/${id}`);
      // Só aplica se a tela ainda estiver neste orçamento (ver `carregar`).
      if (aberto === id) set({ orcamento: dados });
      return true;
    } catch (erro) {
      if (aberto === id) set({ erro });
      return false;
    } finally {
      emVoo -= 1;
      // Destrava só quando a ÚLTIMA mutação volta.
      if (emVoo <= 0) {
        emVoo = 0;
        set({ salvando: false });
      }
    }
  })();

  promessasEmVoo.add(promessa);
  // `finally` no lugar de `then`: sai do conjunto tendo dado certo ou não.
  void promessa.finally(() => promessasEmVoo.delete(promessa));
  return promessa;
}

// ---------------------------------------------------------------- formatação

/*
 * `moedaBr`, `quantidadeBr` e `percentualBr` MUDARAM DE CASA na Story 2.3 —
 * foram para `lib/formato.ts`, sem uma vírgula de mudança no corpo.
 *
 * O motivo é de acoplamento, não de estilo: quem importava `moedaBr` daqui
 * levava junto o módulo inteiro, e o `create<EstadoGerador>()` logo acima roda
 * no import. A tela do dashboard não gera orçamento nenhum e passaria a
 * carregar toda a lógica do gerador no seu chunk.
 *
 * A reexportação abaixo existe para que nenhum consumidor precisasse mudar: os
 * imports de `../estado/gerador.js` continuam valendo. O que ficou aqui é o
 * que de fato pertence ao gerador — as conversões de fronteira de FORMULÁRIO
 * (`percentualParaFracao`, `campoParaFrete`), que só fazem sentido com um
 * campo editável do outro lado.
 */
export { moedaBr, percentualBr, quantidadeBr } from '../lib/formato.js';

/**
 * "10 m² → 4 cx (12,12 m²)" — o arredondamento por caixa fechada, em palavras.
 *
 * A fábrica só vende caixa inteira, então o **faturado** quase nunca é o que o
 * vendedor digitou. Sem esta linha, a diferença entre a quantidade pedida e a
 * cobrada parecia defeito do sistema, e a dúvida chegava por telefone.
 *
 * ## Barra — S11
 *
 * O rodapé é faturado por BARRA de 2,40 m, não pela caixa: "5 m → 3 barras
 * (7,20 m)". O faturado da barra sai com duas casas fixas — é a medida de corte
 * da peça, e "7,2 m" ao lado de "2,40 m" de barra lia como erro de digitação.
 * Quem diz se é barra é o servidor (`unidadeVenda`); a tela não deduz pela
 * classe nem pela unidade.
 *
 * Devolve `undefined` quando não há o que explicar: sem medida, ou quando o
 * pedido já é múltiplo exato dela.
 */
export function dicaCaixaFechada(item: {
  quantidade: string;
  qtdFaturada: string;
  medidaCaixa: string | null;
  unidade: string;
  unidadeVenda?: string;
  medidaArredondamento?: string | null;
}): string | undefined {
  const barra = item.unidadeVenda === 'barra';
  const medida = Number(barra ? item.medidaArredondamento : item.medidaCaixa);
  const faturada = Number(item.qtdFaturada);
  const pedida = Number(item.quantidade);
  if (!Number.isFinite(medida) || medida <= 0) return undefined;
  if (!Number.isFinite(faturada) || !Number.isFinite(pedida)) return undefined;

  const unidade = item.unidade === 'metro_linear' ? 'm' : 'm²';
  const pecas = Math.round(faturada / medida);
  if (pecas <= 0) return undefined;

  const [singular, plural] = barra ? ['barra', 'barras'] : ['caixa', 'caixas'];
  const cx = `${String(pecas)} ${pecas === 1 ? singular : plural}`;
  // Pedido que já fecha caixa exata não precisa da seta: repetir o mesmo número
  // dos dois lados vira ruído e a dica perde força onde ela importa.
  if (Math.abs(faturada - pedida) < 1e-9) {
    return `${cx} — fecha exato`;
  }
  const faturadoTexto = barra ? moedaBr(item.qtdFaturada) : quantidadeBr(item.qtdFaturada);
  return `${quantidadeBr(item.quantidade)} ${unidade} → ${cx} (${faturadoTexto} ${unidade})`;
}

/*
 * As conversões de fronteira do FORMULÁRIO mudaram de casa — `lib/numero.ts`.
 *
 * Estavam aqui, e havia uma segunda cópia da mesma regra na `LinhaItem`. As
 * duas discordavam: a de lá removia o ponto **só quando havia vírgula**, a
 * daqui removia **sempre** — e por isso `1.10` no campo de frete virava
 * R$ 110,00/kg, cem vezes o valor, sem um caractere de aviso.
 *
 * Duas implementações da mesma regra é como se diverge sem perceber. Agora é
 * uma, com faixa e mensagem próprias, e os dois chamadores a usam.
 *
 * A reexportação mantém os imports de `../estado/gerador.js` valendo.
 */
export {
  fracaoParaPercentual,
  freteParaCampo,
  lerFreteRsKg,
  lerRtPercentual,
  numeroBr,
} from '../lib/numero.js';

/**
 * Dois valores monetários são "o mesmo" quando batem **ao centavo**.
 *
 * As células editáveis mostram duas casas, mas o servidor guarda seis
 * (`77,098041` aparece como `77,10`). Comparar as strings acusaria diferença
 * onde não há, e a tela gravaria um override igual ao cálculo — só que
 * arredondado, mudando o total por conta própria. Foi assim que o R1 quebrou:
 * um valor manual virando base no escuro.
 */
export function mesmoCentavo(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.round(x * 100) === Math.round(y * 100);
}
